import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect } from "react";
import { toast } from "sonner";

import { ProductShell } from "@/components/app/product-shell";
import { getBillingStatus, createPortalSession } from "@/lib/billing/checkout.functions";
import { useEntitlements } from "@/lib/billing/use-entitlements";
import { formatPrice } from "@/lib/billing/plans";

type Search = { checkout?: "success" | "cancelled" };

export const Route = createFileRoute("/_authenticated/app/billing")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    checkout:
      search["checkout"] === "success"
        ? "success"
        : search["checkout"] === "cancelled"
          ? "cancelled"
          : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Billing — ArosFlow" },
      {
        name: "description",
        content: "Your ArosFlow plan, monthly lookup usage and payment management.",
      },
      { property: "og:title", content: "Billing — ArosFlow" },
      {
        property: "og:description",
        content: "Your ArosFlow plan, monthly lookup usage and payment management.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: BillingPage,
});

function BillingPage() {
  const { checkout } = useSearch({ from: "/_authenticated/app/billing" });
  const ent = useEntitlements();
  const fetchStatus = useServerFn(getBillingStatus);
  const openPortal = useServerFn(createPortalSession);

  const { data: billing } = useQuery({
    queryKey: ["billing-status"],
    queryFn: () => fetchStatus(),
    staleTime: 300_000,
  });

  useEffect(() => {
    if (checkout === "success") toast.success("Payment received. Your plan is updating.");
    if (checkout === "cancelled") toast.message("Checkout cancelled. Nothing was charged.");
  }, [checkout]);

  const portal = useMutation({
    mutationFn: () => openPortal({ data: { origin: window.location.origin } }),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const enrich = ent.usage.property_enrichment;
  const ai = ent.usage.ai_reasoning;

  return (
    <ProductShell title="Billing" subtitle="Your plan, this month's usage and payment settings.">
      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-xl border border-[var(--glass-border)] bg-card p-5">
          <h2 className="text-sm font-semibold">Current plan</h2>
          <p className="mt-2 text-2xl font-semibold text-gold">
            {ent.planDetail.name}{" "}
            {(ent.planDetail.priceMonthly ?? 0) > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                {formatPrice(ent.planDetail.priceMonthly ?? 0)}/mo
              </span>
            )}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Status: {ent.status}
            {ent.currentPeriodEnd
              ? ` · renews ${new Date(ent.currentPeriodEnd).toLocaleDateString()}`
              : ""}
          </p>

          <dl className="mt-4 space-y-2 text-xs">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Live property lookups</dt>
              <dd>
                {enrich.used} / {enrich.allowance} used
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">AI reasoning runs</dt>
              <dd>
                {ai.used} / {ai.allowance} used
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Purchased credits</dt>
              <dd>{ent.creditsBalance}</dd>
            </div>
          </dl>

          <div className="mt-5 flex flex-wrap gap-2">
            <Link
              to="/pricing"
              className="focus-key rounded-lg gradient-gold px-4 py-2 text-sm font-semibold text-black"
            >
              Change plan
            </Link>
            <button
              type="button"
              onClick={() => portal.mutate()}
              disabled={portal.isPending || !billing?.configured || ent.plan === "free"}
              className="focus-key rounded-lg border border-white/20 px-4 py-2 text-sm font-semibold text-white/85 hover:bg-white/5 disabled:opacity-50"
            >
              {portal.isPending ? "Opening…" : "Manage payment & invoices"}
            </button>
          </div>
          {ent.plan === "free" && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              You have no paid subscription yet, so there is nothing to manage. Choose a plan first
              — cancelling and invoices appear here once a subscription is active.
            </p>
          )}
        </section>

        <section className="rounded-xl border border-[var(--glass-border)] bg-card p-5">
          <h2 className="text-sm font-semibold">Payment status</h2>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            {billing?.message ?? "Checking payment connection…"}
          </p>
          {billing && !billing.configured && (
            <p className="mt-3 text-[11px] leading-relaxed text-warning">
              Card payments are switched off until the payment account is connected. Until then we
              set accounts up manually — nothing on this page pretends to charge a card.
            </p>
          )}
          {billing?.mode === "test" && (
            <p className="mt-3 rounded-md border border-warning/40 bg-warning/5 p-3 text-[11px] text-warning">
              Test mode: use a Stripe test card. No real money moves.
            </p>
          )}
        </section>
      </div>
    </ProductShell>
  );
}
